/*
 * The admin site's words in English: the site section - the attachment library,
 * the promoted articles, the metadata, the settings and the theme thumbnails
 * (S48, extended to the admin site 2026-10-29). Filled in from the components it
 * serves.
 */

import type { ArticlePublishingStatus } from '@accotool-kb/shared';

export const siteEn = {
  /** The attachment library (AttachmentsPage.tsx). */
  attachments: {
    title: 'Attachments',
    intro:
      'Files readers can download. An article lists up to ten and may link any of them from its text; one file can serve many articles.',
    upload: 'Upload files',
    uploadHint: 'Add files to the library',
    noStore: 'This deployment has no attachment store.',
    uploadPicker: 'Choose files for the library',
    replacePicker: 'Choose the replacement file',
    replacedNotice: (fileName: string) =>
      `The file was replaced: every article that lists or links it now offers ${fileName}.`,
    addedNotice: (fileName: string) => `${fileName} was added to the library.`,
    deletedNotice: (fileName: string) => `${fileName} was deleted from the library.`,
    find: 'Find a file',
    findPlaceholder: 'File name…',
    loading: 'Loading the library…',
    empty: 'The library is empty. Upload a file here, or from an article’s editor.',
    noMatch: 'No file matches.',
    /** {date} is the upload's date, {name} who uploaded it. */
    uploaded: 'uploaded {date} by {name}',
    /** {date} is the replacement's date, {name} who replaced it. */
    replaced: 'replaced {date} by {name}',
    notListed: 'not listed',
    notListedHint: 'No article lists it. It can still be linked from a text.',
    listedBy: (count: number) => `listed by ${count === 1 ? '1 article' : `${count} articles`}`,
    replace: 'Replace',
    replaceHint: 'Upload new bytes for this file, for every article that uses it',
    replaceFile: (fileName: string) => `Replace ${fileName}`,
    delete: 'Delete',
    deleteFile: (fileName: string) => `Delete ${fileName}`,
  },

  /** The library's delete dialog (DeleteAttachmentDialog in AttachmentsPage.tsx). */
  deleteAttachment: {
    title: (fileName: string) => `Delete ${fileName}?`,
    cancel: 'Cancel',
    confirm: 'Delete the file',
    finding: 'Finding the articles that use it…',
    unused: 'No article lists it or links to it. Nothing else changes.',
    usedBy: (count: number) =>
      `${count === 1 ? 'One article uses' : `${count} articles use`} this file. Deleting it takes it off every list, and a link to it in a text becomes plain words:`,
    listed: 'listed',
    linked: 'linked in the text',
    permanent: 'The file is removed from storage as well. This cannot be undone.',
  },

  /** The promoted shortlist and its picker (PromotedArticlesPage.tsx). */
  promoted: {
    loading: 'Loading promoted articles…',
    title: 'Promoted articles',
    intro: (max: number) =>
      `Up to ${max} articles shown as tiles above the public article list, in this order. Only live articles actually appear; the rest wait here until they are.`,
    saving: 'Saving…',
    saveOrder: 'Save order',
    saved: 'Promoted articles saved.',
    empty: 'Nothing is promoted. Add an article below.',
    notShown: 'not shown — article is not live',
    alreadyFirst: (title: string) => `${title} is already first.`,
    alreadyLast: (title: string) => `${title} is already last.`,
    moved: (title: string, position: number, total: number) =>
      `Moved ${title} to position ${position} of ${total}.`,
    moveUp: (title: string) => `Move ${title} up`,
    moveDown: (title: string) => `Move ${title} down`,
    removeFrom: (title: string) => `Remove ${title} from the promoted list`,
    remove: 'Remove',
    addTitle: 'Add an article',
    full: (max: number) => `The list is full (${max} of ${max}). Remove one to add another.`,
    searchLabel: 'Search articles to promote',
    searchPlaceholder: 'Search articles…',
    searching: 'Searching…',
    noCandidates: 'Nothing matches that is not already promoted.',
    /** A picker row's tag for an article that is not live: its state, as the API names it. */
    status: (status: ArticlePublishingStatus): string => status,
    add: 'Add',
  },

  /** Product and category tags (MetadataPage.tsx). */
  metadata: {
    loading: 'Loading metadata…',
    title: 'Metadata',
    intro:
      'Product and category tags. Articles reference these, and release notes their product, so changes here are visible everywhere at once.',
    /** `steps` is the server's own account of what the delete did, as it came. */
    deleted: (label: string, steps: string) => `Deleted "${label}". ${steps}`,
    name: 'Name',
    slug: 'Slug',
    articles: 'Articles',
    releaseNotes: 'Release notes',
    state: 'State',
    actions: 'Actions',
    active: 'Active',
    retired: 'Retired',
    edit: 'Edit',
    delete: 'Delete',
    usedByArticles: (articles: number) => `Used by ${articles} article(s). Retiring is usually better.`,
    usedByReleaseNotes: (articles: number, releaseNotes: number) =>
      `Used by ${
        articles > 0 ? `${articles} article(s) and ${releaseNotes} release note(s)` : `${releaseNotes} release note(s)`
      }. A product cannot be deleted while a release note carries it; retire it instead.`,
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    nameRequired: 'A name is required.',
    categoryNameHint: 'Listed comma-separated on an article, so a name cannot contain a comma.',
    /** The lines that name the kind of tag - one set per MetadataKind. */
    product: {
      tab: 'Product tags',
      intro: 'Which products an article or a release note is about. Readers filter on these.',
      newTag: 'New product tag',
      editTag: (name: string) => `Edit product tag: ${name}`,
      none: 'No product tags yet. Articles need at least one.',
      slugHint:
        'The first segment of every article address under this product, the second of every release-note address, and its filter links. Editing it moves every article and release note the product carries.',
      retireHint:
        'Retiring keeps every existing assignment and stops the tag being offered for new ones. This is what you want instead of deleting a tag that is in use.',
    },
    category: {
      tab: 'Category tags',
      intro: 'What kind of article it is — how-to, reference, known issue.',
      newTag: 'New category tag',
      editTag: (name: string) => `Edit category tag: ${name}`,
      none: 'No category tags yet. Articles need at least one.',
      slugHint: 'Used in filter URLs. Editing it changes any saved link that filters on this tag.',
      retireHint:
        'Retiring keeps every existing assignment and stops the tag being offered for new ones. This is what you want instead of deleting a tag that is in use.',
    },
  },

  /** The Settings page (SettingsPage.tsx). The headings it saves are content, not these. */
  settings: {
    loading: 'Loading settings…',
    title: 'Settings',
    intro:
      'How the knowledge base presents itself. A change shows here at once and reaches readers as the public cache expires.',
    saving: 'Saving…',
    save: 'Save',
    unsaved: 'Unsaved changes',
    titleRequired: 'A title is required.',
    tooLong: (max: number) => `At most ${max} characters.`,
    /** The Knowledge Base card: the icon and the site title. */
    knowledgeBase: 'Knowledge Base',
    /** {date} is when the row last changed, {name} who changed it. */
    lastChanged: 'Last changed {date} · {name}',
    icon: 'Icon',
    noIcon: 'No icon uploaded - the built-in mark is shown.',
    removeIcon: 'Remove icon',
    /** `maxSize` is the icon's size limit, written as a size. */
    iconHint: (maxSize: string) =>
      `Shown top-left on every page, at 28 pixels, in place of the “KB” mark. PNG, JPEG, GIF or WEBP, ${maxSize} or smaller. A square image looks best.`,
    /** `limit` is the rule's own sentence (t.admin.rules.siteIconSizeLimit). */
    iconTooLarge: (size: string, limit: string) => `That file is ${size}. ${limit}`,
    iconUnreadable: 'That file could not be read. Choose it again.',
    iconNotAnImage: 'That file is not an image we can store.',
    iconUpdated:
      'Icon updated. You see it now; readers pick it up as the public cache expires, a minute by default.',
    iconRemoved: 'Icon removed. The built-in mark is back.',
    siteTitle: 'Site title',
    siteTitleHint: 'The name in the header of every page and in the browser tab.',
    siteTitleSaved:
      'Saved. You see the new name now; readers pick it up as the public cache expires, a minute by default.',
    /** The Articles and Release Notes cards: a page's own title and the line under it. */
    headingTitle: 'Title',
    headingSubtitle: 'Subtitle',
    /** The Danish column of the two page cards (2026-10-29, S49). */
    headingTitleDanish: 'Danish title',
    headingSubtitleDanish: 'Danish subtitle',
    danishHint: 'Optional. Left empty, Danish readers see the English.',
    /** An empty Danish field's placeholder: the English it stands for. */
    usesEnglish: (english: string) => `Empty: uses “${english}”`,
    usesEnglishNone: 'Empty: no line, as in English',
    readersSee: 'Readers see',
    articles: {
      titleHint: 'The heading of the articles page, its browser tab, and the link back to it from every article.',
      subtitleHint: 'One line under the title on the articles page. Leave it empty for none.',
      saved:
        'Saved. Readers see the new title on the articles page as the public cache expires, a minute by default.',
    },
    releaseNotes: {
      titleHint:
        'The heading of the release-notes page, its browser tab, and the link back to it from every note.',
      subtitleHint: 'One line under the title on the release-notes page. Leave it empty for none.',
      saved:
        'Saved. Readers see the new title on the release-notes page as the public cache expires, a minute by default.',
    },
    /** The Theme card. A theme's name (Classic, Amber, ...) is a name and is not here. */
    theme: 'Theme',
    currentTheme: (theme: string) => `Current: ${theme}`,
    /** `realArticle`: the thumbnails show a published article rather than the built-in sample. */
    thumbnailsHint: (realArticle: boolean) =>
      `Each thumbnail shows ${
        realArticle ? 'a randomly chosen published article' : 'a sample article'
      } in that theme. Click one to try it on this page; nothing changes for readers until you apply it.`,
    applying: 'Applying…',
    applyTheme: 'Apply theme',
    themeApplied: (theme: string) =>
      `The ${theme} theme is applied. You see it now; readers pick it up as the public cache expires, a minute by default.`,
    previewing: (previewed: string, saved: string) =>
      `Previewing ${previewed} on this page only. Readers still see ${saved}.`,
  },

  /** One theme's thumbnail (components/ThemePreview.tsx); the header's links are t.nav's. */
  themePreview: {
    preview: (theme: string) => `Preview the ${theme} theme`,
    primaryAction: 'Primary action',
    secondary: 'Secondary',
    current: 'Current',
    /** The article the thumbnails show while nothing is published. */
    sampleTitle: 'Connecting a data source',
    sampleCategory: 'How-to',
    sampleContent: [
      'A data source is where a report reads its figures from. This guide connects one',
      'and checks that the connection holds.',
      '',
      '## Before you start',
      '',
      '- A user with **read** access to the source system.',
      '- The address of the server, and the name of the database.',
      '',
      '> Connections are tested when you save them, so a wrong password fails here',
      '> rather than in a report at month end.',
      '',
      'Open **Settings** and choose *New data source*. See [the permissions guide](#) for',
      'what the account needs, and use the `Test` button before saving.',
    ].join('\n'),
  },
};
